import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { createEnterprise, getEnterpriseByCode, updateEnterprise } from "../api/enterpriseApi";
import { getBusinessStoreMappingBranches } from "../api/mappingBranchAPI";
import { getBusinessOrganizations } from "../api/businessOrganizationsApi";
import { getAuthHeaders, handleAuthError } from "../api/developerApi";
import { API_BASE_URL } from "../config";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "20px",
    width: "100%",
    maxWidth: "1440px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const embeddedPageStyle = {
    ...pageStyle,
    padding: 0,
};

const emptyValue = "—";

const formatApiError = (error, fallbackMessage) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
        return detail;
    }
    if (Array.isArray(detail) && detail.length) {
        return detail
            .map((item) => {
                if (typeof item === "string") {
                    return item;
                }
                if (item && typeof item === "object") {
                    const location = Array.isArray(item.loc) ? item.loc.join(".") : "";
                    const message = typeof item.msg === "string" ? item.msg : JSON.stringify(item);
                    return location ? `${location}: ${message}` : message;
                }
                return String(item);
            })
            .join(" | ");
    }
    if (detail && typeof detail === "object") {
        return JSON.stringify(detail);
    }
    if (typeof error?.message === "string" && error.message.trim()) {
        return error.message;
    }
    return fallbackMessage;
};

const initialEnterpriseDraft = {
    enterprise_code: "",
    enterprise_name: "",
    branch_id: "",
    catalog_enabled: true,
    data_format: "",
    stock_upload_frequency: "",
    catalog_upload_frequency: "",
    tabletki_login: "",
    tabletki_password: "",
    token: "",
    business_runtime_mode: "baseline",
    has_enterprise_catalog_mappings: false,
    runtime_mode_switch_locked: false,
    runtime_mode_switch_lock_reason: "",
    auto_confirm: false,
};

const initialStoreDraft = {
    store_code: "",
    store_name: "",
    trade_brand: "",
    business_organization_id: "",
    legal_entity_name: "",
    tax_identifier: "",
    is_active: true,
    is_legacy_default: false,
    enterprise_code: "",
    legacy_scope_key: "",
    tabletki_enterprise_code: "",
    tabletki_branch: "",
    salesdrive_enterprise_code: "",
    salesdrive_enterprise_id: "",
    salesdrive_store_name: "",
    catalog_enabled: false,
    stock_enabled: false,
    orders_enabled: false,
    catalog_only_in_stock: true,
    code_strategy: "opaque_mapping",
    code_prefix: "",
    name_strategy: "base",
    extra_markup_enabled: false,
    extra_markup_mode: "percent",
    extra_markup_min: "",
    extra_markup_max: "",
    extra_markup_strategy: "stable_per_product",
    takes_over_legacy_scope: false,
    migration_status: "catalog_stock_live",
};

const codeStrategyOptions = [
    {
        value: "legacy_same",
        label: "Базовая",
        help: "Коды отправляются как есть: внешний код = внутренний product_code.",
    },
    {
        value: "opaque_mapping",
        label: "Индивидуальные коды",
        help: "Для Tabletki используются отдельные коды из маппинга предприятия.",
    },
    {
        value: "prefix_mapping",
        label: "Коды с префиксом",
        help: "Отдельная стратегия с префиксом кода. Использовать только для специальных сценариев.",
    },
];

const nameStrategyOptions = [
    {
        value: "base",
        label: "Базовые",
        help: "Использовать названия из master_catalog.",
    },
    {
        value: "supplier_random",
        label: "Названия поставщиков",
        help: "Использовать сохранённые индивидуальные названия товаров.",
    },
];

const boolShort = (value) => (value ? "Вкл" : "Выкл");

const normalizeOptionalText = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized || null;
};

const normalizeRequiredText = (value, label) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
        throw new Error(`${label} обязательно.`);
    }
    return normalized;
};

const normalizeOptionalDecimal = (value, label) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
        return null;
    }
    const parsed = Number(normalized);
    if (Number.isNaN(parsed) || parsed < 0) {
        throw new Error(`${label} должно быть неотрицательным числом.`);
    }
    return String(parsed);
};

const buildEnterpriseDraft = (enterprise) => ({
    enterprise_code: String(enterprise?.enterprise_code || ""),
    enterprise_name: String(enterprise?.enterprise_name || ""),
    branch_id: String(enterprise?.primary_branch || enterprise?.branch_id || ""),
    catalog_enabled: enterprise?.catalog_enabled === undefined ? true : Boolean(enterprise?.catalog_enabled),
    data_format: String(enterprise?.data_format || ""),
    stock_upload_frequency: enterprise?.stock_upload_frequency ?? "",
    catalog_upload_frequency: enterprise?.catalog_upload_frequency ?? "",
    tabletki_login: String(enterprise?.tabletki_login || ""),
    tabletki_password: String(enterprise?.tabletki_password || ""),
    token: String(enterprise?.token || ""),
    business_runtime_mode: String(enterprise?.business_runtime_mode || "baseline"),
    has_enterprise_catalog_mappings: Boolean(enterprise?.has_enterprise_catalog_mappings),
    runtime_mode_switch_locked: Boolean(enterprise?.runtime_mode_switch_locked),
    runtime_mode_switch_lock_reason: String(enterprise?.runtime_mode_switch_lock_reason || ""),
    auto_confirm: Boolean(enterprise?.auto_confirm),
});

const buildSuggestedStoreCode = (enterprise, existingStores = [], branch = "") => {
    const enterpriseCode = String(enterprise?.enterprise_code || "").trim();
    const normalizedBranch = String(branch || "").trim();
    const baseCode = enterpriseCode
        ? (normalizedBranch ? `business_${enterpriseCode}_${normalizedBranch}` : `business_${enterpriseCode}`)
        : "business_store";
    const usedCodes = new Set(
        existingStores.map((item) => String(item.store_code || "").trim()).filter(Boolean),
    );
    if (!usedCodes.has(baseCode)) {
        return baseCode;
    }
    let counter = 2;
    while (usedCodes.has(`${baseCode}_${counter}`)) {
        counter += 1;
    }
    return `${baseCode}_${counter}`;
};

const buildStoreNameForBranch = (enterprise, branch = "") => {
    const enterpriseName = String(enterprise?.enterprise_name || "").trim() || "Business";
    const normalizedBranch = String(branch || "").trim();
    return normalizedBranch ? `${enterpriseName} / ${normalizedBranch}` : enterpriseName;
};

const buildStoreDraftFromEnterprise = (enterprise, existingStores = []) => ({
    ...initialStoreDraft,
    store_code: buildSuggestedStoreCode(enterprise, existingStores, String(enterprise?.branch_id || "").trim()),
    store_name: buildStoreNameForBranch(enterprise, String(enterprise?.branch_id || "").trim()),
    enterprise_code: String(enterprise?.enterprise_code || "").trim(),
    tabletki_enterprise_code: String(enterprise?.enterprise_code || "").trim(),
    tabletki_branch: String(enterprise?.branch_id || "").trim(),
    catalog_enabled: true,
});

const buildStoreDraftFromStore = (store) => ({
    store_code: String(store?.store_code || ""),
    store_name: String(store?.store_name || ""),
    trade_brand: String(store?.trade_brand || ""),
    business_organization_id: store?.business_organization_id ?? "",
    legal_entity_name: String(store?.legal_entity_name || ""),
    tax_identifier: String(store?.tax_identifier || ""),
    is_active: Boolean(store?.is_active),
    is_legacy_default: Boolean(store?.is_legacy_default),
    enterprise_code: String(store?.enterprise_code || ""),
    legacy_scope_key: String(store?.legacy_scope_key || ""),
    tabletki_enterprise_code: String(store?.tabletki_enterprise_code || ""),
    tabletki_branch: String(store?.tabletki_branch || ""),
    salesdrive_enterprise_code: String(store?.salesdrive_enterprise_code || ""),
    salesdrive_enterprise_id: store?.salesdrive_enterprise_id ?? "",
    salesdrive_store_name: String(store?.salesdrive_store_name || ""),
    catalog_enabled: Boolean(store?.catalog_enabled),
    stock_enabled: Boolean(store?.stock_enabled),
    orders_enabled: Boolean(store?.orders_enabled),
    catalog_only_in_stock: Boolean(store?.catalog_only_in_stock),
    code_strategy: String(store?.code_strategy || "opaque_mapping"),
    code_prefix: String(store?.code_prefix || ""),
    name_strategy: String(store?.name_strategy || "base"),
    extra_markup_enabled: Boolean(store?.extra_markup_enabled),
    extra_markup_mode: String(store?.extra_markup_mode || "percent"),
    extra_markup_min: store?.extra_markup_min ?? "",
    extra_markup_max: store?.extra_markup_max ?? "",
    extra_markup_strategy: String(store?.extra_markup_strategy || "stable_per_product"),
    takes_over_legacy_scope: Boolean(store?.takes_over_legacy_scope),
    migration_status: String(store?.migration_status || "catalog_stock_live"),
});

const buildEnterprisePayload = (draft) => ({
    enterprise_code: normalizeRequiredText(draft.enterprise_code, "enterprise_code"),
    enterprise_name: normalizeRequiredText(draft.enterprise_name, "Название предприятия"),
    branch_id: normalizeRequiredText(draft.branch_id, "Branch ID"),
    data_format: "Business",
    stock_upload_frequency: null,
    catalog_upload_frequency: null,
    tabletki_login: normalizeOptionalText(draft.tabletki_login),
    tabletki_password: normalizeOptionalText(draft.tabletki_password),
    token: normalizeOptionalText(draft.token),
    catalog_enabled: false,
    stock_enabled: false,
    business_runtime_mode: String(draft.business_runtime_mode || "baseline") === "custom"
        ? "custom"
        : "baseline",
    order_fetcher: false,
    auto_confirm: Boolean(draft.auto_confirm),
    stock_correction: false,
});

const buildBusinessEnterpriseSettingsPayload = (draft, { includeEnterpriseCode = false } = {}) => {
    const payload = {
        enterprise_name: normalizeRequiredText(draft.enterprise_name, "Название предприятия"),
        primary_branch: normalizeRequiredText(draft.branch_id, "Основной Tabletki branch"),
        catalog_enabled: Boolean(draft.catalog_enabled),
        tabletki_login: normalizeOptionalText(draft.tabletki_login),
        tabletki_password: normalizeOptionalText(draft.tabletki_password),
        token: normalizeOptionalText(draft.token),
        business_runtime_mode: String(draft.business_runtime_mode || "baseline") === "custom"
            ? "custom"
            : "baseline",
    };
    if (includeEnterpriseCode) {
        payload.enterprise_code = normalizeRequiredText(draft.enterprise_code, "enterprise_code");
    }
    return payload;
};

const buildStorePayload = (draft, selectedEnterpriseCode, { isBaselineEnterprise = false } = {}) => ({
    store_code: normalizeRequiredText(draft.store_code, "store_code"),
    store_name: normalizeRequiredText(draft.store_name, "store_name"),
    trade_brand: normalizeOptionalText(draft.trade_brand),
    business_organization_id: String(draft.business_organization_id).trim() === ""
        ? null
        : Number(draft.business_organization_id),
    legal_entity_name: normalizeOptionalText(draft.legal_entity_name),
    tax_identifier: normalizeOptionalText(draft.tax_identifier),
    is_active: Boolean(draft.is_active),
    is_legacy_default: isBaselineEnterprise ? true : Boolean(draft.is_legacy_default),
    enterprise_code: normalizeOptionalText(selectedEnterpriseCode),
    legacy_scope_key: normalizeOptionalText(draft.legacy_scope_key),
    tabletki_enterprise_code: normalizeOptionalText(draft.tabletki_enterprise_code) || normalizeOptionalText(selectedEnterpriseCode),
    tabletki_branch: normalizeOptionalText(draft.tabletki_branch),
    salesdrive_enterprise_code: normalizeOptionalText(draft.salesdrive_enterprise_code),
    salesdrive_enterprise_id: String(draft.salesdrive_enterprise_id).trim() === ""
        ? null
        : Number(draft.salesdrive_enterprise_id),
    salesdrive_store_name: normalizeOptionalText(draft.salesdrive_store_name),
    // Deprecated compatibility field: catalog is now operator-managed on the
    // enterprise level, but backend/storage still carries BusinessStore.catalog_enabled.
    catalog_enabled: true,
    stock_enabled: Boolean(draft.stock_enabled),
    orders_enabled: Boolean(draft.orders_enabled),
    catalog_only_in_stock: Boolean(draft.catalog_only_in_stock),
    code_strategy: isBaselineEnterprise ? "legacy_same" : String(draft.code_strategy || "opaque_mapping"),
    code_prefix: isBaselineEnterprise ? null : normalizeOptionalText(draft.code_prefix),
    name_strategy: isBaselineEnterprise ? "base" : String(draft.name_strategy || "base"),
    extra_markup_enabled: Boolean(draft.extra_markup_enabled),
    extra_markup_mode: "percent",
    extra_markup_min: normalizeOptionalDecimal(draft.extra_markup_min, "Минимальная наценка"),
    extra_markup_max: normalizeOptionalDecimal(draft.extra_markup_max, "Максимальная наценка"),
    extra_markup_strategy: "stable_per_product",
    takes_over_legacy_scope: Boolean(draft.takes_over_legacy_scope),
    migration_status: String(draft.migration_status || "catalog_stock_live"),
});

const Section = ({ title, description, children, actions = null }) => (
    <div className="og-card og-toolbar" style={{ display: "grid", gap: "18px" }}>
        <div className="th">
            <div>
                <h2>{title}</h2>
                {description ? <p>{description}</p> : null}
            </div>
            {actions ? <div className="acts">{actions}</div> : null}
        </div>
        {children}
    </div>
);

const Field = ({ label, children, helpText }) => (
    <div className="og-fld">
        <label>{label}</label>
        {children}
        {helpText ? <span className="help">{helpText}</span> : null}
    </div>
);

const InfoItem = ({ label, value }) => (
    <div className="og-fld">
        <label>{label}</label>
        <div className="val">{value || emptyValue}</div>
    </div>
);

const dynamicMarkupDays = [
    [1, "Пн"], [2, "Вт"], [3, "Ср"], [4, "Чт"], [5, "Пт"], [6, "Сб"], [7, "Вс"],
];

const emptyDynamicMarkupScheduleDraft = {
    name: "",
    is_active: true,
    period_type: "weekly_window",
    weekdays: [1, 2, 3, 4, 5, 6, 7],
    start_time: "10:00",
    end_time: "18:00",
    starts_at: "",
    ends_at: "",
    markup_min: "7",
    markup_max: "10",
    excluded_supplier_codes: [],
};

const toLocalDateTimeInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const buildDynamicScheduleDraft = (schedule) => ({
    name: String(schedule?.name || ""),
    is_active: schedule?.is_active === undefined ? true : Boolean(schedule.is_active),
    period_type: String(schedule?.period_type || "weekly_window"),
    weekdays: Array.isArray(schedule?.weekdays) ? schedule.weekdays.map(Number) : [1, 2, 3, 4, 5, 6, 7],
    start_time: String(schedule?.start_time || "10:00").slice(0, 5),
    end_time: String(schedule?.end_time || "18:00").slice(0, 5),
    starts_at: toLocalDateTimeInput(schedule?.starts_at),
    ends_at: toLocalDateTimeInput(schedule?.ends_at),
    markup_min: schedule?.markup_min ?? "7",
    markup_max: schedule?.markup_max ?? "10",
    excluded_supplier_codes: Array.isArray(schedule?.excluded_supplier_codes) ? schedule.excluded_supplier_codes.map(String) : [],
});

const DynamicMarkupSchedulePanel = ({ storeId }) => {
    const [schedules, setSchedules] = useState([]);
    const [status, setStatus] = useState(null);
    const [runs, setRuns] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [supplierSearch, setSupplierSearch] = useState("");
    const [draft, setDraft] = useState(emptyDynamicMarkupScheduleDraft);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const load = useCallback(async () => {
        if (!storeId) return;
        setLoading(true);
        try {
            const [rulesResponse, statusResponse, runsResponse, suppliersResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/business-stores/${storeId}/dynamic-markup-schedules`, getAuthHeaders()),
                axios.get(`${API_BASE_URL}/business-stores/${storeId}/dynamic-markup-status`, getAuthHeaders()),
                axios.get(`${API_BASE_URL}/business-stores/${storeId}/dynamic-markup-runs?limit=10`, getAuthHeaders()),
                axios.get(`${API_BASE_URL}/dropship/enterprises/`, getAuthHeaders()),
            ]);
            setSchedules(rulesResponse.data || []);
            setStatus(statusResponse.data || null);
            setRuns(runsResponse.data || []);
            setSuppliers(suppliersResponse.data || []);
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось загрузить расписание ценовой поправки."));
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    useEffect(() => {
        setDraft(emptyDynamicMarkupScheduleDraft);
        setEditingId(null);
        setError("");
        setSuccess("");
        load();
    }, [load]);

    const buildPayload = () => {
        const common = {
            name: normalizeRequiredText(draft.name, "Название правила"),
            is_active: Boolean(draft.is_active),
            period_type: draft.period_type,
            markup_min: Number(draft.markup_min),
            markup_max: Number(draft.markup_max),
            excluded_supplier_codes: [...new Set((draft.excluded_supplier_codes || []).map(String).filter(Boolean))],
        };
        if (Number.isNaN(common.markup_min) || Number.isNaN(common.markup_max)) {
            throw new Error("Укажите корректный диапазон наценки.");
        }
        if (common.period_type === "weekly_window") {
            return { ...common, weekdays: draft.weekdays, start_time: draft.start_time, end_time: draft.end_time };
        }
        if (!draft.starts_at || !draft.ends_at) {
            throw new Error("Укажите начало и окончание периода по датам.");
        }
        return {
            ...common,
            starts_at: new Date(draft.starts_at).toISOString(),
            ends_at: new Date(draft.ends_at).toISOString(),
        };
    };

    const save = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = buildPayload();
            if (editingId) {
                await axios.put(`${API_BASE_URL}/business-stores/${storeId}/dynamic-markup-schedules/${editingId}`, payload, getAuthHeaders());
            } else {
                await axios.post(`${API_BASE_URL}/business-stores/${storeId}/dynamic-markup-schedules`, payload, getAuthHeaders());
            }
            setDraft(emptyDynamicMarkupScheduleDraft);
            setEditingId(null);
            setSuccess(editingId ? "Правило расписания обновлено." : "Правило расписания создано.");
            await load();
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось сохранить правило."));
        } finally {
            setSaving(false);
        }
    };

    const deactivate = async (scheduleId) => {
        setError("");
        try {
            await axios.delete(`${API_BASE_URL}/business-stores/${storeId}/dynamic-markup-schedules/${scheduleId}`, getAuthHeaders());
            setSuccess("Правило деактивировано; история применений сохранена.");
            await load();
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось деактивировать правило."));
        }
    };

    const updateWeekday = (day) => {
        setDraft((prev) => ({
            ...prev,
            weekdays: prev.weekdays.includes(day)
                ? prev.weekdays.filter((item) => item !== day)
                : [...prev.weekdays, day].sort((a, b) => a - b),
        }));
    };

    const toggleExcludedSupplier = (supplierCode) => {
        setDraft((prev) => {
            const excluded = prev.excluded_supplier_codes || [];
            return {
                ...prev,
                excluded_supplier_codes: excluded.includes(supplierCode)
                    ? excluded.filter((code) => code !== supplierCode)
                    : [...excluded, supplierCode],
            };
        });
    };

    const supplierByCode = useMemo(
        () => new Map(suppliers.map((supplier) => [String(supplier.code), supplier])),
        [suppliers],
    );
    const normalizedSupplierSearch = supplierSearch.trim().toLowerCase();
    const availableSuppliers = suppliers.filter((supplier) => {
        const code = String(supplier.code || "");
        const name = String(supplier.name || "");
        return !draft.excluded_supplier_codes.includes(code)
            && (!normalizedSupplierSearch || `${code} ${name}`.toLowerCase().includes(normalizedSupplierSearch));
    });

    return (
        <Section
            title="Расписание ценовой поправки"
            description="Поправка добавляется поверх обычной цены Tabletki. Для активного правила система выбирает новый процент при каждой реальной выгрузке остатков; фиксированную долю заказов это не гарантирует. Prom и Rozetka это расписание не используют."
            actions={<button type="button" className="og-btn og-btn-ghost" onClick={load} disabled={loading}>{loading ? "Загрузка..." : "Обновить"}</button>}
        >
            {error ? <div className="og-callout danger"><span className="ic">⚠</span><div>{error}</div></div> : null}
            {success ? <div className="og-callout"><span className="ic">✓</span><div>{success}</div></div> : null}
            <div className="og-callout">
                <span className="ic">ℹ</span>
                <div>
                    Сейчас: {status?.active_schedule ? `${status.active_schedule.name} активно` : "активного правила нет"}. Последняя попытка: {status?.last_run ? `+${status.last_run.selected_markup_percent}% (${status.last_run.status})` : "ещё не было"}.
                </div>
            </div>
            <div className="og-block">
                <div className="og-bh"><span className="ic">⏱</span><h4>{editingId ? "Редактирование правила" : "Новое правило"}</h4></div>
                <div className="og-grid2">
                    <Field label="Название правила"><input className="og-inp" value={draft.name} onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))} placeholder="Иванович: день" /></Field>
                    <Field label="Режим периода"><select className="og-inp" value={draft.period_type} onChange={(event) => setDraft((prev) => ({ ...prev, period_type: event.target.value }))}><option value="weekly_window">По дням недели</option><option value="date_window">По датам</option></select></Field>
                    <Field label="Мин. доп. наценка, %"><input className="og-inp" type="number" step="0.01" min="0" max="100" value={draft.markup_min} onChange={(event) => setDraft((prev) => ({ ...prev, markup_min: event.target.value }))} /></Field>
                    <Field label="Макс. доп. наценка, %"><input className="og-inp" type="number" step="0.01" min="0" max="100" value={draft.markup_max} onChange={(event) => setDraft((prev) => ({ ...prev, markup_max: event.target.value }))} /></Field>
                    {draft.period_type === "weekly_window" ? <>
                        <Field label="Начало"><input className="og-inp" type="time" value={draft.start_time} onChange={(event) => setDraft((prev) => ({ ...prev, start_time: event.target.value }))} /></Field>
                        <Field label="Окончание"><input className="og-inp" type="time" value={draft.end_time} onChange={(event) => setDraft((prev) => ({ ...prev, end_time: event.target.value }))} helpText="Если окончание раньше начала, правило продолжается после полуночи." /></Field>
                        <div className="og-col-span"><Field label="Дни недели"><div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>{dynamicMarkupDays.map(([day, label]) => <button key={day} type="button" className={`og-btn og-btn-sm ${draft.weekdays.includes(day) ? "og-btn-primary" : "og-btn-ghost"}`} onClick={() => updateWeekday(day)}>{label}</button>)}</div></Field></div>
                    </> : <>
                        <Field label="Начало"><input className="og-inp" type="datetime-local" value={draft.starts_at} onChange={(event) => setDraft((prev) => ({ ...prev, starts_at: event.target.value }))} /></Field>
                        <Field label="Окончание"><input className="og-inp" type="datetime-local" value={draft.ends_at} onChange={(event) => setDraft((prev) => ({ ...prev, ends_at: event.target.value }))} /></Field>
                    </>}
                    <div className="og-col-span">
                        <Field label="Не применять к поставщикам" helpText="Исключение действует только для этой временной ценовой поправки; базовые правила поставщика сохраняются.">
                            <input className="og-inp" value={supplierSearch} onChange={(event) => setSupplierSearch(event.target.value)} placeholder="Поиск по коду или названию поставщика" />
                            {draft.excluded_supplier_codes.length ? <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
                                {draft.excluded_supplier_codes.map((code) => {
                                    const supplier = supplierByCode.get(code);
                                    return <button key={code} type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => toggleExcludedSupplier(code)}>{code}{supplier?.name ? ` — ${supplier.name}` : ""} ×</button>;
                                })}
                            </div> : <span className="help">Не выбрано: правило применяется ко всем поставщикам.</span>}
                            {availableSuppliers.length ? <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px", maxHeight: "112px", overflowY: "auto" }}>
                                {availableSuppliers.slice(0, 30).map((supplier) => <button key={supplier.code} type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => toggleExcludedSupplier(String(supplier.code))}>{supplier.code} — {supplier.name}{supplier.is_active === false ? " (неактивен)" : ""}</button>)}
                            </div> : null}
                        </Field>
                    </div>
                    <button type="button" className={`og-check${draft.is_active ? " ck" : ""}`} onClick={() => setDraft((prev) => ({ ...prev, is_active: !prev.is_active }))}><span className="box">{draft.is_active ? "✓" : ""}</span><span>Правило активно</span></button>
                </div>
                <div className="acts" style={{ marginTop: "14px" }}>
                    <button type="button" className="og-btn og-btn-primary" onClick={save} disabled={saving}>{saving ? "Сохранение..." : editingId ? "Сохранить правило" : "Создать правило"}</button>
                    {editingId ? <button type="button" className="og-btn og-btn-ghost" onClick={() => { setEditingId(null); setDraft(emptyDynamicMarkupScheduleDraft); }}>Отмена</button> : null}
                </div>
            </div>
            <div className="og-tbl-wrap"><table className="og-tbl"><thead><tr>{["Правило", "Период", "Диапазон", "Поставщики", "Статус", ""].map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{schedules.length ? schedules.map((item) => <tr key={item.id}><td className="strong">{item.name}</td><td>{item.period_type === "weekly_window" ? `${(item.weekdays || []).map((day) => dynamicMarkupDays.find(([id]) => id === Number(day))?.[1]).join(", ")} ${String(item.start_time).slice(0, 5)}–${String(item.end_time).slice(0, 5)}` : `${new Date(item.starts_at).toLocaleString()} – ${new Date(item.ends_at).toLocaleString()}`}</td><td>+{item.markup_min}…{item.markup_max}%</td><td>{item.excluded_supplier_codes?.length ? `Исключено: ${item.excluded_supplier_codes.length}` : "Все поставщики"}</td><td><span className={item.is_active ? "og-pill g" : "og-pill off"}>{item.is_active ? "Включено" : "Выключено"}</span></td><td className="go"><button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => { setEditingId(item.id); setDraft(buildDynamicScheduleDraft(item)); }}>Изменить</button>{item.is_active ? <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => deactivate(item.id)}>Выключить</button> : null}</td></tr>) : <tr><td colSpan="6">Правил пока нет.</td></tr>}</tbody></table></div>
            {runs.length ? <div className="og-tbl-wrap"><table className="og-tbl"><thead><tr>{["Последняя попытка", "Наценка", "Запуск", "Статус"].map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{runs.map((run) => <tr key={run.id}><td>{new Date(run.created_at).toLocaleString()}</td><td>+{run.selected_markup_percent}%</td><td>{run.trigger}</td><td><span className={run.status === "published" ? "og-pill g" : run.status === "failed" ? "og-pill off" : "og-pill"}>{run.status}</span>{run.error ? ` — ${run.error}` : ""}</td></tr>)}</tbody></table></div> : null}
        </Section>
    );
};

const OVERRIDE_MODES = [
    ["fixed_price", "Фиксированная цена, грн"],
    ["rrp_discount_percent", "Скидка от РРЦ, %"],
    ["fix_discount_percent", "Снизить сумму фикса, %"],
    ["fix_add_uah", "Своя сумма фикса, грн"],
    ["base_thr", "Свой базовый порог (доля)"],
];
const overrideModeLabel = (mode) => OVERRIDE_MODES.find(([key]) => key === mode)?.[1] || mode;
const overrideStatusPill = (status) => ({
    active: ["og-pill g", "Действует"],
    scheduled: ["og-pill", "Запланировано"],
    expired: ["og-pill off", "Истекло"],
    inactive: ["og-pill off", "Выключено"],
}[status] || ["og-pill", status]);

const emptyProductOverrideDraft = {
    calculation_type: "price_override",
    scope_type: "product",
    scope_value: "",
    product_code: "",
    supplier_code: "",
    mode: "fixed_price",
    value: "",
    valid_until: "",
    apply_to_tabletki: true,
    apply_to_marketplaces: true,
    skip_markups_and_jitter: true,
    reason: "",
};

const parseBulkOverrideLines = (text, defaults) => {
    // Формат строки: SKU; значение; [срок YYYY-MM-DD]; [поставщик]; [причина]. Режим — из формы.
    const items = [];
    const errors = [];
    String(text || "").split(/\r?\n/).forEach((line, index) => {
        const raw = line.trim();
        if (!raw) return;
        const parts = raw.split(/[;\t]/).map((part) => part.trim());
        const [sku, value, until, supplier, reason] = parts;
        if (!sku || !value || Number.isNaN(Number(value))) {
            errors.push(`Строка ${index + 1}: нужны SKU и число.`);
            return;
        }
        items.push({
            product_code: sku,
            supplier_code: supplier || defaults.supplier_code || null,
            mode: defaults.mode,
            value: String(value).replace(",", "."),
            valid_until: until ? new Date(`${until}T23:59:59`).toISOString() : (defaults.valid_until ? new Date(defaults.valid_until).toISOString() : null),
            apply_to_tabletki: defaults.apply_to_tabletki,
            apply_to_marketplaces: defaults.apply_to_marketplaces,
            skip_markups_and_jitter: defaults.skip_markups_and_jitter,
            reason: reason || defaults.reason || null,
        });
    });
    return { items, errors };
};

const ProductPriceOverridesPanel = ({ storeId }) => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState("active");
    const [query, setQuery] = useState("");
    const [draft, setDraft] = useState(emptyProductOverrideDraft);
    const [bulkText, setBulkText] = useState("");
    const [bulkOpen, setBulkOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [manufacturerOptions, setManufacturerOptions] = useState([]);
    const [manufacturerQuery, setManufacturerQuery] = useState("");
    useEffect(() => {
        if (draft.scope_type !== "manufacturer" || !storeId) return undefined;
        const timer = setTimeout(async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/business-stores/${storeId}/price-rule-manufacturers`, { ...getAuthHeaders(), params: { q: manufacturerQuery || undefined, limit: 50 } });
                setManufacturerOptions(response.data || []);
            } catch (_) { setManufacturerOptions([]); }
        }, 250);
        return () => clearTimeout(timer);
    }, [storeId, draft.scope_type, manufacturerQuery]);

    const load = useCallback(async () => {
        if (!storeId) return;
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/business-stores/${storeId}/product-price-overrides`, {
                ...getAuthHeaders(),
                params: { status: statusFilter, q: query.trim() || undefined, limit: 500 },
            });
            setItems(response.data?.items || []);
            setTotal(response.data?.total || 0);
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось загрузить индивидуальные цены."));
        } finally {
            setLoading(false);
        }
    }, [storeId, statusFilter, query]);

    useEffect(() => {
        setError("");
        setSuccess("");
        load();
    }, [load]);

    const buildPayload = () => {
        const scopeType = draft.scope_type === "manufacturer" ? "manufacturer" : "product";
        const scopeValue = scopeType === "product"
            ? normalizeRequiredText(draft.product_code, "SKU")
            : normalizeRequiredText(draft.scope_value, "Производитель");
        const value = Number(String(draft.value).replace(",", "."));
        if (Number.isNaN(value) || value < 0) throw new Error("Укажите корректное значение.");
        if (draft.mode === "fixed_price" && !draft.valid_until) throw new Error("Для фиксированной цены нужен срок действия.");
        return {
            product_code: scopeType === "product" ? scopeValue : "",
            scope_type: scopeType,
            scope_value: scopeValue,
            calculation_type: draft.calculation_type,
            supplier_code: normalizeOptionalText(draft.supplier_code),
            mode: draft.mode,
            value: String(draft.value).replace(",", "."),
            valid_until: draft.valid_until ? new Date(draft.valid_until).toISOString() : null,
            apply_to_tabletki: Boolean(draft.apply_to_tabletki),
            apply_to_marketplaces: Boolean(draft.apply_to_marketplaces),
            skip_markups_and_jitter: Boolean(draft.skip_markups_and_jitter),
            reason: normalizeOptionalText(draft.reason),
        };
    };

    const save = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = buildPayload();
            await axios.post(`${API_BASE_URL}/business-stores/${storeId}/product-price-overrides`, payload, getAuthHeaders());
            setSuccess(`Правило для ${payload.scope_value} сохранено. Цена изменится при следующем пересчёте офферов.`);
            setDraft((prev) => ({ ...prev, product_code: "", scope_value: "", value: "" }));
            await load();
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось сохранить правило."));
        } finally {
            setSaving(false);
        }
    };

    const saveBulk = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const { items: parsed, errors } = parseBulkOverrideLines(bulkText, draft);
            if (!parsed.length) throw new Error(errors[0] || "Нет строк для загрузки.");
            const response = await axios.post(`${API_BASE_URL}/business-stores/${storeId}/product-price-overrides/bulk`, { items: parsed }, getAuthHeaders());
            const data = response.data || {};
            const allErrors = [...errors, ...(data.errors || [])];
            setSuccess(`Создано ${data.created || 0}, обновлено ${data.updated || 0}.${allErrors.length ? ` Пропущено: ${allErrors.length}.` : ""}`);
            if (allErrors.length) setError(allErrors.slice(0, 10).join(" · "));
            setBulkText("");
            await load();
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось загрузить список."));
        } finally {
            setSaving(false);
        }
    };

    const deactivate = async (id) => {
        setSaving(true);
        setError("");
        try {
            await axios.patch(`${API_BASE_URL}/business-stores/${storeId}/product-price-overrides/${id}`, {}, getAuthHeaders());
            await load();
        } catch (requestError) {
            handleAuthError(requestError);
            setError(formatApiError(requestError, "Не удалось выключить правило."));
        } finally {
            setSaving(false);
        }
    };

    const valueLabel = draft.calculation_type === "cost_from_rrp_discount" ? "Скидка от РРЦ, %" : { fixed_price: "Цена, грн", rrp_discount_percent: "Скидка от РРЦ, %", fix_discount_percent: "Снижение, %", fix_add_uah: "Сумма фикса, грн", base_thr: "Порог, доля (0.02 = 2%)" }[draft.mode];
    const checkButton = (key, label) => (
        <button type="button" className={`og-check${draft[key] ? " ck" : ""}`} onClick={() => setDraft((prev) => ({ ...prev, [key]: !prev[key] }))}><span className="box">{draft[key] ? "✓" : ""}</span><span>{label}</span></button>
    );

    return (
        <Section
            title="Индивидуальные цены товаров"
            description="Правило на SKU этого магазина: фиксированная цена (слив срочных товаров), своя сумма фикса, снижение фикса в процентах или свой базовый порог. РРЦ поставщика сильнее правила. Режимы порога меняют только нижнюю границу цены; если цена шла под конкурента выше порога, она не изменится."
            actions={<button type="button" className="og-btn og-btn-ghost" onClick={load} disabled={loading}>{loading ? "Загрузка..." : "Обновить"}</button>}
        >
            {error ? <div className="og-callout danger"><span className="ic">⚠</span><div>{error}</div></div> : null}
            {success ? <div className="og-callout"><span className="ic">✓</span><div>{success}</div></div> : null}
            <div className="og-block">
                <div className="og-bh"><span className="ic">₴</span><h4>Новое правило</h4></div>
                <div className="og-grid2">
                    <Field label="Тип расчёта"><select className="og-inp" value={draft.calculation_type} onChange={(event) => setDraft((prev) => ({ ...prev, calculation_type: event.target.value, mode: event.target.value === "cost_from_rrp_discount" ? "rrp_discount_percent" : "fixed_price", valid_until: "" }))}><option value="price_override">Правило цены</option><option value="cost_from_rrp_discount">Себестоимость от РРЦ</option></select></Field>
                    <Field label="Уровень правила"><select className="og-inp" value={draft.scope_type} onChange={(event) => setDraft((prev) => ({ ...prev, scope_type: event.target.value, scope_value: "", product_code: "" }))}><option value="product">Товар (SKU)</option><option value="manufacturer">Производитель</option></select></Field>
                    {draft.scope_type === "product" ? <Field label="SKU (код мастер-каталога)"><input className="og-inp" value={draft.product_code} onChange={(event) => setDraft((prev) => ({ ...prev, product_code: event.target.value }))} placeholder="1040102" /></Field> : <Field label="Производитель"><><input className="og-inp" list={`manufacturer-options-${storeId}`} value={manufacturerQuery} onChange={(event) => { setManufacturerQuery(event.target.value); setDraft((prev) => ({ ...prev, scope_value: event.target.value })); }} placeholder="Начните вводить название" /><datalist id={`manufacturer-options-${storeId}`}>{manufacturerOptions.map((item) => <option key={item.manufacturer} value={item.manufacturer}>{item.sku_count} SKU</option>)}</datalist></></Field>}
                    <Field label="Поставщик" helpText="Пусто — все поставщики магазина."><input className="og-inp" value={draft.supplier_code} onChange={(event) => setDraft((prev) => ({ ...prev, supplier_code: event.target.value.toUpperCase() }))} placeholder="D2" /></Field>
                    {draft.calculation_type === "price_override" ? <Field label="Режим"><select className="og-inp" value={draft.mode} onChange={(event) => setDraft((prev) => ({ ...prev, mode: event.target.value }))}>{OVERRIDE_MODES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></Field> : <div className="help">Себестоимость считается как РРЦ × (1 − скидка / 100), после чего применяются обычные правила ценообразования магазина.</div>}
                    <Field label={valueLabel}><input className="og-inp" type="number" step="0.01" min="0" value={draft.value} onChange={(event) => setDraft((prev) => ({ ...prev, value: event.target.value }))} /></Field>
                    {draft.calculation_type === "price_override" ? <Field label="Действует до" helpText={draft.mode === "fixed_price" ? "Обязательно для фиксированной цены." : "Необязательно; пусто — бессрочно."}><input className="og-inp" type="datetime-local" value={draft.valid_until} onChange={(event) => setDraft((prev) => ({ ...prev, valid_until: event.target.value }))} /></Field> : null}
                    <Field label="Причина"><input className="og-inp" value={draft.reason} onChange={(event) => setDraft((prev) => ({ ...prev, reason: event.target.value }))} placeholder="Срок годности до 30.11" /></Field>
                    <div className="og-col-span" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {draft.calculation_type === "price_override" ? <>{checkButton("apply_to_tabletki", "Tabletki")}{checkButton("apply_to_marketplaces", "Prom / Rozetka / Kasta")}{draft.mode === "fixed_price" ? checkButton("skip_markups_and_jitter", "Без jitter и доп. наценок") : null}</> : null}
                    </div>
                </div>
                <div className="acts" style={{ marginTop: "14px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button type="button" className="og-btn og-btn-primary" onClick={save} disabled={saving}>{saving ? "Сохранение..." : "Сохранить правило"}</button>
                    <button type="button" className="og-btn og-btn-ghost" onClick={() => setBulkOpen((prev) => !prev)}>{bulkOpen ? "Скрыть массовую загрузку" : "Массовая загрузка списком"}</button>
                </div>
                {bulkOpen ? (
                    <div style={{ marginTop: "12px" }}>
                        <Field label="Список строк" helpText="Одна строка — один товар: SKU; значение; срок YYYY-MM-DD (необязательно); поставщик (необязательно); причина. Режим, каналы и значения по умолчанию берутся из формы выше.">
                            <textarea className="og-inp" rows={6} value={bulkText} onChange={(event) => setBulkText(event.target.value)} placeholder={"1040102; 399; 2026-11-30; ; срок годности\n1084018; 1190; 2026-11-30"} />
                        </Field>
                        <button type="button" className="og-btn og-btn-primary" onClick={saveBulk} disabled={saving}>{saving ? "Загрузка..." : "Загрузить список"}</button>
                    </div>
                ) : null}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginBottom: "8px" }}>
                <select className="og-inp" style={{ width: "auto" }} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="active">Действующие</option>
                    <option value="expired">Истекшие</option>
                    <option value="inactive">Выключенные</option>
                    <option value="all">Все</option>
                </select>
                <input className="og-inp" style={{ width: "220px" }} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск SKU / производитель" />
                <span className="help">Всего: {total}</span>
            </div>
            <div className="og-tbl-wrap"><table className="og-tbl"><thead><tr>{["Уровень", "SKU / производитель", "Товар", "Поставщик", "Режим", "Значение", "До", "Каналы", "Текущая цена / закупка", "Статус", ""].map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{items.length ? items.map((item) => {
                const [pillClass, pillLabel] = overrideStatusPill(item.status);
                return <tr key={item.id}>
                    <td>{item.scope_type === "manufacturer" ? "Производитель" : "SKU"}</td>
                    <td className="strong">{item.scope_value || item.product_code}</td>
                    <td>{item.product_name || "—"}</td>
                    <td>{item.supplier_code ? `${item.supplier_code}${item.supplier_name ? ` — ${item.supplier_name}` : ""}` : "Все"}</td>
                    <td>{item.calculation_type === "cost_from_rrp_discount" ? "Себестоимость от РРЦ" : overrideModeLabel(item.mode)}</td>
                    <td>{item.calculation_type === "cost_from_rrp_discount" ? `РРЦ − ${item.value}%` : item.value}</td>
                    <td>{item.valid_until ? new Date(item.valid_until).toLocaleString() : "—"}</td>
                    <td>{[item.apply_to_tabletki ? "Tabletki" : null, item.apply_to_marketplaces ? "Маркетплейсы" : null].filter(Boolean).join(", ")}</td>
                    <td>{item.current_offer_price != null ? `${item.current_offer_price} / ${item.current_offer_wholesale ?? "—"}` : "нет оффера"}</td>
                    <td><span className={pillClass}>{pillLabel}</span>{item.reason ? <div className="help">{item.reason}</div> : null}</td>
                    <td className="go">{item.is_active ? <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => deactivate(item.id)} disabled={saving}>Выключить</button> : null}</td>
                </tr>;
            }) : <tr><td colSpan="10">{loading ? "Загрузка..." : "Правил пока нет."}</td></tr>}</tbody></table></div>
        </Section>
    );
};

const BusinessStoresPage = ({ embedded = false, mode = "all" }) => {
    const [businessEnterprises, setBusinessEnterprises] = useState([]);
    const [businessOrganizations, setBusinessOrganizations] = useState([]);
    const [stores, setStores] = useState([]);
    const [mappingBranches, setMappingBranches] = useState([]);
    const [selectedEnterpriseCode, setSelectedEnterpriseCode] = useState("");
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [storeDraftStoreId, setStoreDraftStoreId] = useState(null);
    const [isCreatingEnterprise, setIsCreatingEnterprise] = useState(false);
    const [enterpriseDraft, setEnterpriseDraft] = useState(initialEnterpriseDraft);
    const [storeDraft, setStoreDraft] = useState(initialStoreDraft);
    const [enterpriseSaving, setEnterpriseSaving] = useState(false);
    const [storeSaving, setStoreSaving] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState("");
    const [enterpriseError, setEnterpriseError] = useState("");
    const [enterpriseSuccess, setEnterpriseSuccess] = useState("");
    const [storeError, setStoreError] = useState("");
    const [storeSuccess, setStoreSuccess] = useState("");
    const [enterpriseCatalogOnlyInStockDraft, setEnterpriseCatalogOnlyInStockDraft] = useState(true);
    const [mappingBranchesLoading, setMappingBranchesLoading] = useState(false);
    const [mappingBranchesError, setMappingBranchesError] = useState("");
    const enterpriseContextRequestIdRef = useRef(0);
    const storeDraftDirtyRef = useRef(false);

    const loadBusinessEnterprises = useCallback(async () => {
        const response = await axios.get(
            `${API_BASE_URL}/business-stores/meta/business-enterprises`,
            getAuthHeaders(),
        );
        const rows = response.data || [];
        setBusinessEnterprises(rows);
        return rows;
    }, []);

    const loadStores = useCallback(async () => {
        const response = await axios.get(`${API_BASE_URL}/business-stores`, getAuthHeaders());
        const rows = response.data || [];
        setStores(rows);
        return rows;
    }, []);

    const loadBusinessOrganizations = useCallback(async () => {
        const rows = await getBusinessOrganizations({ is_active: true });
        setBusinessOrganizations(rows || []);
        return rows || [];
    }, []);

    const loadMappingBranches = useCallback(async (enterpriseCode) => {
        const normalizedEnterpriseCode = String(enterpriseCode || "").trim();
        if (!normalizedEnterpriseCode) {
            setMappingBranches([]);
            return [];
        }
        const rows = await getBusinessStoreMappingBranches(normalizedEnterpriseCode);
        setMappingBranches(rows || []);
        return rows || [];
    }, []);

    const loadBusinessEnterpriseSettings = useCallback(async (enterpriseCode) => {
        const normalizedEnterpriseCode = String(enterpriseCode || "").trim();
        if (!normalizedEnterpriseCode) {
            return null;
        }
        try {
            const response = await axios.get(
                `${API_BASE_URL}/business-enterprise-settings/${normalizedEnterpriseCode}`,
                getAuthHeaders(),
            );
            return response.data || null;
        } catch (error) {
            if (error?.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }, []);

    const replaceStoreDraft = useCallback((draft, ownerStoreId = null, { dirty = false } = {}) => {
        setStoreDraft(draft);
        setStoreDraftStoreId(ownerStoreId);
        storeDraftDirtyRef.current = Boolean(dirty);
    }, []);

    const reloadMeta = useCallback(async () => {
        const [enterpriseRows] = await Promise.all([
            loadBusinessEnterprises(),
            loadStores(),
            loadBusinessOrganizations(),
        ]);
        return enterpriseRows;
    }, [loadBusinessEnterprises, loadBusinessOrganizations, loadStores]);

    useEffect(() => {
        async function bootstrap() {
            setPageLoading(true);
            setPageError("");
            try {
                const enterpriseRows = await reloadMeta();
                if (!selectedEnterpriseCode && enterpriseRows.length > 0) {
                    setSelectedEnterpriseCode(String(enterpriseRows[0].enterprise_code || ""));
                }
            } catch (error) {
                handleAuthError(error);
                console.error("Error loading Business Stores page:", error);
                setPageError(formatApiError(error, "Не удалось загрузить настройки магазинов."));
            } finally {
                setPageLoading(false);
            }
        }

        bootstrap();
    }, [reloadMeta, selectedEnterpriseCode]);

    useEffect(() => {
        async function loadBranchOptions() {
            if (!selectedEnterpriseCode || isCreatingEnterprise) {
                setMappingBranches([]);
                setMappingBranchesError("");
                return;
            }

            setMappingBranchesLoading(true);
            setMappingBranchesError("");
            try {
                await loadMappingBranches(selectedEnterpriseCode);
            } catch (error) {
                handleAuthError(error);
                console.error("Error loading mapping branches:", error);
                setMappingBranches([]);
                setMappingBranchesError(formatApiError(error, "Не удалось загрузить branch-список Business-контура."));
            } finally {
                setMappingBranchesLoading(false);
            }
        }

        loadBranchOptions();
    }, [isCreatingEnterprise, loadMappingBranches, selectedEnterpriseCode]);

    const selectedEnterpriseMeta = useMemo(
        () => businessEnterprises.find((item) => item.enterprise_code === selectedEnterpriseCode) || null,
        [businessEnterprises, selectedEnterpriseCode],
    );

    const selectedBusinessOrganization = useMemo(
        () => businessOrganizations.find((item) => String(item.id) === String(storeDraft.business_organization_id)) || null,
        [businessOrganizations, storeDraft.business_organization_id],
    );

    const storesForSelectedEnterprise = useMemo(
        () => stores.filter((item) => item.enterprise_code === selectedEnterpriseCode),
        [stores, selectedEnterpriseCode],
    );

    const mappingBranchOptions = useMemo(
        () => (mappingBranches || []).filter((item) => String(item.branch || "").trim()),
        [mappingBranches],
    );

    const mappingBranchValues = useMemo(
        () => new Set(mappingBranchOptions.map((item) => String(item.branch || "").trim()).filter(Boolean)),
        [mappingBranchOptions],
    );

    const currentStoreBranchMissingFromOptions = useMemo(() => {
        const currentBranch = String(storeDraft.tabletki_branch || "").trim();
        if (!currentBranch) {
            return false;
        }
        return !mappingBranchValues.has(currentBranch);
    }, [mappingBranchValues, storeDraft.tabletki_branch]);

    const catalogScopeStoreCandidates = useMemo(() => {
        const targetBranch = String(enterpriseDraft.branch_id || "").trim();
        if (!selectedEnterpriseCode || !targetBranch) {
            return [];
        }
        return storesForSelectedEnterprise.filter(
            (item) => Boolean(item.is_active) && String(item.tabletki_branch || "").trim() === targetBranch,
        );
    }, [enterpriseDraft.branch_id, selectedEnterpriseCode, storesForSelectedEnterprise]);

    const catalogScopeStore = useMemo(
        () => (catalogScopeStoreCandidates.length === 1 ? catalogScopeStoreCandidates[0] : null),
        [catalogScopeStoreCandidates],
    );


    const isNewStoreDraft = !selectedStoreId;
    const isBaselineEnterprise = enterpriseDraft.business_runtime_mode !== "custom";
    const isCustomEnterprise = !isBaselineEnterprise;
    const isRuntimeModeLocked = isCustomEnterprise && Boolean(enterpriseDraft.runtime_mode_switch_locked);
    const identityControlsDisabled = isBaselineEnterprise;
    const storeRoutingReadOnly = isBaselineEnterprise;
    const enterpriseStrategyStoreId = catalogScopeStore?.id || selectedStoreId || null;
    const effectiveCodeStrategy = isBaselineEnterprise ? "legacy_same" : String(storeDraft.code_strategy || "opaque_mapping");
    const effectiveNameStrategy = isBaselineEnterprise ? "base" : String(storeDraft.name_strategy || "base");
    const runtimeModeHelpText = isRuntimeModeLocked
        ? (enterpriseDraft.runtime_mode_switch_lock_reason || "Для підприємства вже створені індивідуальні коди або назви каталогу. Зміну режиму заблоковано.")
        : (isCustomEnterprise
            ? "Каталог, остатки, заказы и статусы используют настраиваемые коды предприятия."
            : "Каталог, остатки, заказы и статусы используют базовые коды без маппинга.");
    const showEnterpriseSections = mode !== "stores";
    const showStoreSections = mode !== "enterprises";
    const pageTitle = mode === "enterprises"
        ? "Business-предприятия"
        : mode === "stores"
            ? "Магазины"
            : "Настройки магазинов";
    const pageDescription = mode === "enterprises"
        ? "Business-only настройки предприятий, runtime-режимы и связанный список магазинов."
        : mode === "stores"
            ? "Магазины Business-контура, branch-маппинг и привязки к юрлицам."
            : "Основная operational page для enterprise runtime и store overlays Business-контура.";

    useEffect(() => {
        if (catalogScopeStore) {
            setEnterpriseCatalogOnlyInStockDraft(Boolean(catalogScopeStore.catalog_only_in_stock));
        } else {
            setEnterpriseCatalogOnlyInStockDraft(true);
        }
    }, [catalogScopeStore]);

    useEffect(() => {
        if (!isBaselineEnterprise) {
            return;
        }
        setStoreDraft((prev) => {
            if (
                prev.is_legacy_default === true
                && prev.code_strategy === "legacy_same"
                && prev.name_strategy === "base"
                && !prev.code_prefix
            ) {
                return prev;
            }
            return {
                ...prev,
                is_legacy_default: true,
                code_strategy: "legacy_same",
                code_prefix: "",
                name_strategy: "base",
            };
        });
    }, [isBaselineEnterprise]);

    useEffect(() => {
        if (!isNewStoreDraft || !selectedEnterpriseCode || isCreatingEnterprise) {
            return;
        }

        const currentBranch = String(storeDraft.tabletki_branch || "").trim();
        if (currentBranch && mappingBranchValues.has(currentBranch)) {
            return;
        }

        const preferredBranch = (
            mappingBranchOptions.find((item) => Boolean(item.is_primary_enterprise_branch))?.branch
            || mappingBranchOptions[0]?.branch
            || String(enterpriseDraft.branch_id || "").trim()
        );
        if (!preferredBranch) {
            return;
        }

        replaceStoreDraft({
            ...storeDraft,
            store_code: buildSuggestedStoreCode(enterpriseDraft, storesForSelectedEnterprise, preferredBranch),
            store_name: buildStoreNameForBranch(enterpriseDraft, preferredBranch),
            enterprise_code: String(enterpriseDraft.enterprise_code || "").trim(),
            tabletki_enterprise_code: String(enterpriseDraft.enterprise_code || "").trim(),
            tabletki_branch: preferredBranch,
            is_legacy_default: isBaselineEnterprise,
            code_strategy: isBaselineEnterprise ? "legacy_same" : "opaque_mapping",
            name_strategy: isBaselineEnterprise ? "base" : "supplier_random",
        }, null, { dirty: false });
    }, [
        enterpriseDraft,
        isNewStoreDraft,
        isBaselineEnterprise,
        isCreatingEnterprise,
        mappingBranchOptions,
        mappingBranchValues,
        replaceStoreDraft,
        selectedEnterpriseCode,
        storeDraft,
        storeDraft.tabletki_branch,
        storesForSelectedEnterprise,
    ]);

    useEffect(() => {
        const requestId = enterpriseContextRequestIdRef.current + 1;
        enterpriseContextRequestIdRef.current = requestId;
        let cancelled = false;

        const isLatestRequest = () => !cancelled && enterpriseContextRequestIdRef.current === requestId;

        async function loadEnterpriseContext() {
            if (isCreatingEnterprise) {
                return;
            }
            if (!selectedEnterpriseCode) {
                setEnterpriseDraft(initialEnterpriseDraft);
                replaceStoreDraft(initialStoreDraft, null, { dirty: false });
                setSelectedStoreId(null);
                return;
            }

            try {
                const [legacyEnterprise, businessEnterpriseSettings] = await Promise.all([
                    getEnterpriseByCode(selectedEnterpriseCode),
                    loadBusinessEnterpriseSettings(selectedEnterpriseCode),
                ]);
                if (!isLatestRequest()) {
                    return;
                }
                const enterprise = {
                    ...legacyEnterprise,
                    ...(businessEnterpriseSettings || {}),
                    branch_id: businessEnterpriseSettings?.primary_branch || legacyEnterprise?.branch_id,
                    auto_confirm: legacyEnterprise?.auto_confirm,
                    has_enterprise_catalog_mappings: legacyEnterprise?.has_enterprise_catalog_mappings,
                    runtime_mode_switch_locked: legacyEnterprise?.runtime_mode_switch_locked,
                    runtime_mode_switch_lock_reason: legacyEnterprise?.runtime_mode_switch_lock_reason,
                };
                setEnterpriseDraft(buildEnterpriseDraft(enterprise));

                const storeForSelection = storesForSelectedEnterprise.find(
                    (item) => item.id === selectedStoreId,
                ) || null;

                if (storeForSelection) {
                    if (!storeDraftDirtyRef.current) {
                        replaceStoreDraft(buildStoreDraftFromStore(storeForSelection), storeForSelection.id, { dirty: false });
                    }
                } else if (storesForSelectedEnterprise.length > 0) {
                    if (storeDraftDirtyRef.current) {
                        return;
                    }
                    const firstStore = storesForSelectedEnterprise[0];
                    setSelectedStoreId(firstStore.id);
                    replaceStoreDraft(buildStoreDraftFromStore(firstStore), firstStore.id, { dirty: false });
                } else {
                    if (storeDraftDirtyRef.current) {
                        return;
                    }
                    setSelectedStoreId(null);
                    replaceStoreDraft(buildStoreDraftFromEnterprise(enterprise, storesForSelectedEnterprise), null, { dirty: false });
                }
            } catch (error) {
                if (!isLatestRequest()) {
                    return;
                }
                handleAuthError(error);
                console.error("Error loading selected Business enterprise:", error);
                setPageError(formatApiError(error, "Не удалось загрузить данные выбранного Business-предприятия."));
            }
        }

        loadEnterpriseContext();
        return () => {
            cancelled = true;
        };
    }, [isCreatingEnterprise, loadBusinessEnterpriseSettings, replaceStoreDraft, selectedEnterpriseCode, selectedStoreId, storesForSelectedEnterprise]);

    const onEnterpriseChange = (key, value) => {
        setEnterpriseDraft((prev) => ({ ...prev, [key]: value }));
    };

    const onStoreChange = (key, value) => {
        storeDraftDirtyRef.current = true;
        setStoreDraft((prev) => ({ ...prev, [key]: value }));
    };

    const selectOverlay = (store) => {
        setStoreError("");
        setStoreSuccess("");
        setSelectedStoreId(store.id);
        replaceStoreDraft(buildStoreDraftFromStore(store), store.id, { dirty: false });
    };

    const handleSaveEnterprise = async () => {
        setEnterpriseSaving(true);
        setEnterpriseError("");
        setEnterpriseSuccess("");
        try {
            const payload = buildEnterprisePayload(enterpriseDraft);
            const businessSettingsPayload = buildBusinessEnterpriseSettingsPayload(enterpriseDraft);
            if (isCreatingEnterprise) {
                await createEnterprise(payload);
                await axios.put(
                    `${API_BASE_URL}/business-enterprise-settings/${payload.enterprise_code}`,
                    businessSettingsPayload,
                    getAuthHeaders(),
                );
            } else {
                await updateEnterprise(selectedEnterpriseCode, payload);
                await axios.put(
                    `${API_BASE_URL}/business-enterprise-settings/${selectedEnterpriseCode}`,
                    businessSettingsPayload,
                    getAuthHeaders(),
                );
            }
            if (catalogScopeStore) {
                await axios.put(
                    `${API_BASE_URL}/business-stores/${catalogScopeStore.id}`,
                    { catalog_only_in_stock: Boolean(enterpriseCatalogOnlyInStockDraft) },
                    getAuthHeaders(),
                );
            }
            if (enterpriseStrategyStoreId) {
                await axios.put(
                    `${API_BASE_URL}/business-stores/${enterpriseStrategyStoreId}`,
                    {
                        is_legacy_default: isBaselineEnterprise ? true : Boolean(storeDraft.is_legacy_default),
                        code_strategy: isBaselineEnterprise ? "legacy_same" : String(storeDraft.code_strategy || "opaque_mapping"),
                        name_strategy: isBaselineEnterprise ? "base" : String(storeDraft.name_strategy || "base"),
                        code_prefix: isBaselineEnterprise ? null : normalizeOptionalText(storeDraft.code_prefix),
                    },
                    getAuthHeaders(),
                );
            }
            const [updatedLegacyEnterprise, updatedBusinessEnterpriseSettings] = await Promise.all([
                getEnterpriseByCode(payload.enterprise_code),
                loadBusinessEnterpriseSettings(payload.enterprise_code),
                loadBusinessEnterprises(),
                loadStores(),
                loadMappingBranches(payload.enterprise_code),
            ]);
            const updatedEnterprise = {
                ...updatedLegacyEnterprise,
                ...(updatedBusinessEnterpriseSettings || {}),
                branch_id: updatedBusinessEnterpriseSettings?.primary_branch || updatedLegacyEnterprise?.branch_id,
                auto_confirm: updatedLegacyEnterprise?.auto_confirm,
                has_enterprise_catalog_mappings: updatedLegacyEnterprise?.has_enterprise_catalog_mappings,
                runtime_mode_switch_locked: updatedLegacyEnterprise?.runtime_mode_switch_locked,
                runtime_mode_switch_lock_reason: updatedLegacyEnterprise?.runtime_mode_switch_lock_reason,
            };
            setIsCreatingEnterprise(false);
            setSelectedEnterpriseCode(payload.enterprise_code);
            setEnterpriseDraft(buildEnterpriseDraft(updatedEnterprise));
            setStoreDraft((prev) => ({
                ...prev,
                enterprise_code: String(updatedEnterprise.enterprise_code || ""),
                tabletki_branch: prev.tabletki_branch || String(updatedEnterprise.branch_id || ""),
            }));
            setEnterpriseSuccess(isCreatingEnterprise
                ? "Business-предприятие создано."
                : "Настройки предприятия и ассортимента каталога сохранены.");
        } catch (error) {
            handleAuthError(error);
            console.error("Error saving enterprise settings:", error);
            setEnterpriseError(formatApiError(error, "Не удалось сохранить предприятие."));
        } finally {
            setEnterpriseSaving(false);
        }
    };

    const handleSaveStore = async () => {
        setStoreSaving(true);
        setStoreError("");
        setStoreSuccess("");
        try {
            if (selectedStoreId && selectedStoreId !== storeDraftStoreId) {
                const message = (
                    "Сохранение заблокировано: данные формы относятся к другому магазину. "
                    + "Выберите магазин ещё раз и повторите изменение."
                );
                console.warn("BusinessStore draft owner mismatch", {
                    selectedStoreId,
                    storeDraftStoreId,
                    store_code: storeDraft.store_code,
                });
                setStoreError(message);
                return;
            }

            const payload = buildStorePayload(storeDraft, selectedEnterpriseCode, { isBaselineEnterprise });
            if (selectedBusinessOrganization) {
                const organizationSalesDriveId = Number(selectedBusinessOrganization.salesdrive_organization_id);
                payload.legal_entity_name = normalizeOptionalText(selectedBusinessOrganization.short_name);
                payload.tax_identifier = normalizeOptionalText(selectedBusinessOrganization.tax_id);
                payload.salesdrive_enterprise_id = Number.isInteger(organizationSalesDriveId)
                    ? organizationSalesDriveId
                    : null;
            }
            let response;
            if (!selectedStoreId) {
                response = await axios.post(`${API_BASE_URL}/business-stores`, payload, getAuthHeaders());
            } else {
                const updatePayload = { ...payload };
                delete updatePayload.store_code;
                response = await axios.put(
                    `${API_BASE_URL}/business-stores/${selectedStoreId}`,
                    updatePayload,
                    getAuthHeaders(),
                );
            }

            const savedStore = response.data;
            await loadStores();
            setSelectedStoreId(savedStore.id);
            replaceStoreDraft(buildStoreDraftFromStore(savedStore), savedStore.id, { dirty: false });
            setStoreSuccess(!selectedStoreId
                ? "Магазин создан."
                : "Изменения магазина сохранены.");
        } catch (error) {
            handleAuthError(error);
            console.error("Error saving Business Store:", error);
            setStoreError(formatApiError(error, "Не удалось сохранить настройки магазина."));
        } finally {
            setStoreSaving(false);
        }
    };

    const selectBusinessEnterprise = (enterpriseCode) => {
        setIsCreatingEnterprise(false);
        setSelectedEnterpriseCode(enterpriseCode);
        setSelectedStoreId(null);
        replaceStoreDraft(initialStoreDraft, null, { dirty: false });
        setEnterpriseError("");
        setEnterpriseSuccess("");
        setStoreError("");
        setStoreSuccess("");
    };

    const startNewBusinessEnterprise = () => {
        setIsCreatingEnterprise(true);
        setSelectedEnterpriseCode("");
        setSelectedStoreId(null);
        setMappingBranches([]);
        setEnterpriseDraft({
            ...initialEnterpriseDraft,
            data_format: "Business",
            auto_confirm: false,
            business_runtime_mode: "baseline",
        });
        replaceStoreDraft(initialStoreDraft, null, { dirty: false });
        setEnterpriseError("");
        setEnterpriseSuccess("");
        setStoreError("");
        setStoreSuccess("");
    };

    const startNewBusinessStore = () => {
        if (!selectedEnterpriseCode) {
            setStoreError("Сначала выберите или создайте Business-предприятие.");
            return;
        }
        setSelectedStoreId(null);
        replaceStoreDraft(buildStoreDraftFromEnterprise(enterpriseDraft, storesForSelectedEnterprise), null, { dirty: false });
        setStoreError("");
        setStoreSuccess("");
    };

    return (
        <div style={embedded ? embeddedPageStyle : pageStyle}>
            {!embedded && (
                <div className="og-card og-toolbar">
                    <div className="th">
                        <div>
                            <h2>{pageTitle}</h2>
                            <p>{pageDescription}</p>
                            <p>Сначала настраивается предприятие, затем — конкретные магазины.</p>
                        </div>
                    </div>
                </div>
            )}

            {pageError ? (
                <div className="og-callout danger">
                    <span className="ic">⚠</span>
                    <div>{pageError}</div>
                </div>
            ) : null}

            <div className={mode === "enterprises" || mode === "stores" ? "og-split" : undefined} style={mode === "enterprises" || mode === "stores" ? undefined : { display: "grid", gap: "20px" }}>
                <div className={mode === "enterprises" || mode === "stores" ? "og-picker" : undefined}>
            <Section
                title="Предприятия"
                description="В списке только data_format=Business."
                actions={(
                    <button type="button" className="og-btn og-btn-ghost" onClick={startNewBusinessEnterprise}>
                        Новое Business-предприятие
                    </button>
                )}
            >
                    <Field label="Business-предприятие">
                        <select
                            className="og-inp"
                            value={isCreatingEnterprise ? "" : selectedEnterpriseCode}
                            onChange={(event) => selectBusinessEnterprise(event.target.value)}
                            disabled={pageLoading}
                        >
                            <option value="">Выберите Business-предприятие</option>
                            {businessEnterprises.map((item) => (
                                <option key={item.enterprise_code} value={item.enterprise_code}>
                                    {item.enterprise_name} ({item.enterprise_code})
                                </option>
                            ))}
                        </select>
                    </Field>
                    {showEnterpriseSections ? (
                        <div className="og-list">
                            {businessEnterprises.map((item) => (
                                <button
                                    key={item.enterprise_code}
                                    type="button"
                                    className={`og-list-item${item.enterprise_code === selectedEnterpriseCode ? " sel" : ""}`}
                                    onClick={() => selectBusinessEnterprise(item.enterprise_code)}
                                    disabled={pageLoading}
                                >
                                    <span className="nm">{item.enterprise_name || item.enterprise_code}</span>
                                    <span className="meta">Код: {item.enterprise_code}</span>
                                    <span className="badges">
                                        <span className="og-badge info">Business</span>
                                        <span className="og-badge info">settings</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : null}

                    {!selectedEnterpriseCode && !isCreatingEnterprise ? (
                        <div className="og-callout warn">
                            <span className="ic">⚠</span>
                            <div>Выберите Business-предприятие или создайте новое в этом разделе.</div>
                        </div>
                    ) : null}

                    {showStoreSections && selectedEnterpriseCode ? (
                        <div className="og-block">
                            <div className="og-bh">
                                <h4>Магазины</h4>
                                <button type="button" className="og-btn og-btn-ghost og-btn-sm" style={{ marginLeft: "auto" }} onClick={startNewBusinessStore}>
                                    Новый магазин
                                </button>
                            </div>
                            {storesForSelectedEnterprise.length > 0 ? (
                                <div className="og-list">
                                    {storesForSelectedEnterprise.map((store) => (
                                        <button
                                            key={store.id}
                                            type="button"
                                            className={`og-list-item${store.id === selectedStoreId ? " sel" : ""}`}
                                            onClick={() => selectOverlay(store)}
                                        >
                                            <span className="nm">{store.store_name || store.store_code}</span>
                                            <span className="meta">Код: {store.store_code}</span>
                                            <span className="meta">Branch: {store.tabletki_branch || emptyValue}</span>
                                            <span className="badges">
                                                {store.is_active ? <span className="og-badge on"><span className="dot"></span>active</span> : null}
                                                {store.stock_enabled ? <span className="og-pill g">stock</span> : null}
                                                {store.orders_enabled ? <span className="og-pill g">orders</span> : null}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="og-callout">
                                    <span className="ic">ℹ</span>
                                    <div>Для предприятия пока нет магазинов.</div>
                                </div>
                            )}
                        </div>
                    ) : null}
            </Section>
                </div>

                <div style={{ display: "grid", gap: "20px", minWidth: 0 }}>
            {(selectedEnterpriseCode || isCreatingEnterprise) ? (
                <>
                    {selectedEnterpriseMeta && !showStoreSections ? (
                        <div className="og-card" style={{ padding: "16px 20px", display: "grid", gap: "10px" }}>
                            <h2 style={{ margin: 0, fontSize: "15px", letterSpacing: "-.01em" }}>Карточка предприятия</h2>
                            <div className="og-grid4">
                                <InfoItem label="Предприятие" value={selectedEnterpriseMeta.enterprise_name} />
                                <InfoItem label="Код" value={selectedEnterpriseMeta.enterprise_code} />
                                <InfoItem label="Branch каталога" value={enterpriseDraft.branch_id || emptyValue} />
                                <InfoItem label="Режим" value={isCustomEnterprise ? "Настраиваемый" : "Базовый"} />
                            </div>
                            <InfoItem label="Runtime" value="Управляется в Business Settings" />
                        </div>
                    ) : null}

                    {enterpriseError ? (
                        <div className="og-callout danger">
                            <span className="ic">⚠</span>
                            <div>{enterpriseError}</div>
                        </div>
                    ) : null}
                    {enterpriseSuccess ? (
                        <div className="og-callout good">
                            <span className="ic">✓</span>
                            <div>{enterpriseSuccess}</div>
                        </div>
                    ) : null}

                    {showEnterpriseSections ? (
                    <Section
                        title="Настройки Business-предприятия"
                        description="Здесь хранятся identity, credentials и включение ежедневной передачи каталога. Остатки и заказы включаются на уровне магазинов."
                        actions={(
                            <button
                                type="button"
                                className="og-btn og-btn-primary"
                                onClick={handleSaveEnterprise}
                                disabled={enterpriseSaving}
                            >
                                {enterpriseSaving ? "Сохранение..." : "Сохранить предприятие"}
                            </button>
                        )}
                    >
                        <div className="og-block">
                            <div className="og-bh"><span className="ic">📝</span><h4>Основные данные</h4></div>
                            <div className="og-grid3">
                                <Field label="Код предприятия">
                                    <input
                                        className="og-inp"
                                        value={enterpriseDraft.enterprise_code}
                                        onChange={(event) => onEnterpriseChange("enterprise_code", event.target.value)}
                                        readOnly={!isCreatingEnterprise}
                                    />
                                </Field>
                                <Field label="Название предприятия">
                                    <input
                                        className="og-inp"
                                        value={enterpriseDraft.enterprise_name}
                                        onChange={(event) => onEnterpriseChange("enterprise_name", event.target.value)}
                                    />
                                </Field>
                                <Field label="Основной Tabletki branch">
                                    <input
                                        className="og-inp"
                                        value={enterpriseDraft.branch_id}
                                        onChange={(event) => onEnterpriseChange("branch_id", event.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>
                        <div className="og-block">
                            <div className="og-bh"><span className="ic">⚙</span><h4>Переключатели</h4></div>
                            <div className="og-grid2">
                                <button
                                    type="button"
                                    className={`og-check${enterpriseDraft.catalog_enabled ? " ck" : ""}`}
                                    onClick={() => onEnterpriseChange("catalog_enabled", !enterpriseDraft.catalog_enabled)}
                                >
                                    <span className="box">{enterpriseDraft.catalog_enabled ? "✓" : ""}</span>
                                    <span>Передавать каталог предприятия на Tabletki</span>
                                </button>
                                <button
                                    type="button"
                                    className={`og-check${enterpriseDraft.auto_confirm ? " ck" : ""}`}
                                    onClick={() => onEnterpriseChange("auto_confirm", !enterpriseDraft.auto_confirm)}
                                >
                                    <span className="box">{enterpriseDraft.auto_confirm ? "✓" : ""}</span>
                                    <span>Автобронирование</span>
                                </button>
                            </div>
                        </div>
                        <div className="og-block">
                            <div className="og-bh"><span className="ic">🏷</span><h4>Стратегии кодов и названий</h4><span className="hint">относится к магазинам предприятия</span></div>
                            <div className="og-grid2">
                                <Field
                                    label="Режим"
                                    helpText={runtimeModeHelpText}
                                >
                                    <select
                                        className="og-inp"
                                        value={enterpriseDraft.business_runtime_mode || "baseline"}
                                        onChange={(event) => onEnterpriseChange("business_runtime_mode", event.target.value)}
                                        disabled={isRuntimeModeLocked}
                                    >
                                        <option value="baseline">Базовый</option>
                                        <option value="custom">Настраиваемый</option>
                                    </select>
                                </Field>
                                <Field
                                    label="Стратегия кодов"
                                    helpText={codeStrategyOptions.find((item) => item.value === effectiveCodeStrategy)?.help}
                                >
                                    <select
                                        className="og-inp"
                                        value={effectiveCodeStrategy}
                                        onChange={(event) => onStoreChange("code_strategy", event.target.value)}
                                        disabled={identityControlsDisabled}
                                    >
                                        {codeStrategyOptions.map((item) => (
                                            <option key={item.value} value={item.value}>{item.label}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field
                                    label="Стратегия названий"
                                    helpText={nameStrategyOptions.find((item) => item.value === effectiveNameStrategy)?.help}
                                >
                                    <select
                                        className="og-inp"
                                        value={effectiveNameStrategy}
                                        onChange={(event) => onStoreChange("name_strategy", event.target.value)}
                                        disabled={identityControlsDisabled}
                                    >
                                        {nameStrategyOptions.map((item) => (
                                            <option key={item.value} value={item.value}>{item.label}</option>
                                        ))}
                                    </select>
                                </Field>
                                <div className="og-col-span">
                                    <button
                                        type="button"
                                        className={`og-check${enterpriseCatalogOnlyInStockDraft ? " ck" : ""}${!catalogScopeStore ? " disabled" : ""}`}
                                        disabled={!catalogScopeStore}
                                        onClick={() => setEnterpriseCatalogOnlyInStockDraft(!enterpriseCatalogOnlyInStockDraft)}
                                    >
                                        <span className="box">{enterpriseCatalogOnlyInStockDraft ? "✓" : ""}</span>
                                        <span>В каталог только товары с остатком главного магазина</span>
                                    </button>
                                </div>
                                {effectiveCodeStrategy === "prefix_mapping" ? (
                                    <Field label="Префикс кода">
                                        <input
                                            className="og-inp"
                                            value={storeDraft.code_prefix}
                                            onChange={(event) => onStoreChange("code_prefix", event.target.value)}
                                            disabled={identityControlsDisabled}
                                        />
                                    </Field>
                                ) : null}
                            </div>
                        </div>
                        <div className="og-block">
                            <div className="og-bh"><span className="ic">🔐</span><h4>Доступ предприятия</h4></div>
                            <div className="og-grid3">
                                <Field label="Логин Tabletki">
                                    <input
                                        className="og-inp"
                                        value={enterpriseDraft.tabletki_login}
                                        onChange={(event) => onEnterpriseChange("tabletki_login", event.target.value)}
                                    />
                                </Field>
                                <Field label="Пароль Tabletki">
                                    <input
                                        type="password"
                                        className="og-inp"
                                        value={enterpriseDraft.tabletki_password}
                                        onChange={(event) => onEnterpriseChange("tabletki_password", event.target.value)}
                                    />
                                </Field>
                                <Field label="SalesDrive API key" helpText="Оставьте поле пустым, чтобы не менять текущий токен.">
                                    <input
                                        type="password"
                                        className="og-inp"
                                        value={enterpriseDraft.token}
                                        onChange={(event) => onEnterpriseChange("token", event.target.value)}
                                        placeholder="Введите новый токен"
                                    />
                                </Field>
                            </div>
                        </div>
                    </Section>
                    ) : null}

                    {storeError ? (
                        <div className="og-callout danger">
                            <span className="ic">⚠</span>
                            <div>{storeError}</div>
                        </div>
                    ) : null}
                    {storeSuccess ? (
                        <div className="og-callout good">
                            <span className="ic">✓</span>
                            <div>{storeSuccess}</div>
                        </div>
                    ) : null}

                    {showStoreSections && selectedEnterpriseCode && mode === "all" ? (
                    <Section
                        title="Branch-маппинг предприятия"
                        description="Рабочий список branch из Business-маппинга для выбранного Business-предприятия."
                    >
                        {mappingBranchOptions.length > 0 ? (
                            <div className="og-tbl-wrap">
                                <table className="og-tbl">
                                    <thead>
                                        <tr>
                                            {["Branch", "Внешний store / mapping", "Роль"].map((header) => (
                                                <th key={header}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mappingBranchOptions.map((item) => (
                                            <tr key={item.branch}>
                                                <td className="strong">{item.branch}</td>
                                                <td>{item.mapping_store_hint || emptyValue}</td>
                                                <td>
                                                    {item.is_primary_enterprise_branch
                                                        ? <span className="og-pill g">Основной branch предприятия</span>
                                                        : <span className="og-pill">Branch магазина</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="og-callout warn">
                                <span className="ic">⚠</span>
                                <div>Для выбранного предприятия нет branch в Business-маппинге. При сохранении магазина branch будет создан автоматически.</div>
                            </div>
                        )}
                    </Section>
                    ) : null}

                    {showStoreSections && selectedEnterpriseCode ? (
                    <Section
                        title={isNewStoreDraft ? "Новый магазин Business-контура" : "Магазин Business-контура"}
                        description={isNewStoreDraft
                            ? "Магазин ещё не сохранён. Он будет создан после нажатия «Сохранить магазин»."
                            : `Редактируется магазин: ${storeDraft.store_code || emptyValue}`}
                        actions={(
                            <button
                                type="button"
                                className="og-btn og-btn-primary"
                                onClick={handleSaveStore}
                                disabled={storeSaving}
                            >
                                {storeSaving ? "Сохранение..." : (isNewStoreDraft ? "Сохранить магазин" : "Сохранить изменения")}
                            </button>
                        )}
                    >
                        {enterpriseDraft.business_runtime_mode === "baseline" ? (
                            <div className="og-callout">
                                <span className="ic">ℹ</span>
                                <div>Для предприятия в базовом режиме коды и названия фиксированы как базовые: без маппинга для каталога, остатков, заказов и статусов.</div>
                            </div>
                        ) : null}
                        <div className="og-block">
                            <div className="og-bh"><span className="ic">🏬</span><h4>Данные магазина</h4></div>
                            <div className="og-grid2">
                                <Field label="Код магазина">
                                    <input
                                        className="og-inp"
                                        value={storeDraft.store_code}
                                        onChange={(event) => onStoreChange("store_code", event.target.value)}
                                        readOnly={!isNewStoreDraft}
                                    />
                                </Field>
                                <Field label="Название магазина">
                                    <input
                                        className="og-inp"
                                        value={storeDraft.store_name}
                                        onChange={(event) => onStoreChange("store_name", event.target.value)}
                                    />
                                </Field>
                                <Field label="Торговая марка">
                                    <input
                                        className="og-inp"
                                        value={storeDraft.trade_brand}
                                        onChange={(event) => onStoreChange("trade_brand", event.target.value)}
                                    />
                                </Field>
                                <Field label="Юрлицо">
                                    <select
                                        className="og-inp"
                                        value={storeDraft.business_organization_id}
                                        onChange={(event) => onStoreChange("business_organization_id", event.target.value)}
                                    >
                                        <option value="">Не выбрана</option>
                                        {businessOrganizations.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {`${item.short_name} / SalesDrive ${item.salesdrive_organization_id || "—"} / ${item.tax_id || "без ЕДРПОУ"}`}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <div className="og-col-span">
                                    <Field label="Данные юрлица">
                                        <input
                                            className="og-inp"
                                            value={selectedBusinessOrganization
                                                ? `${selectedBusinessOrganization.short_name || ""} / ${selectedBusinessOrganization.tax_id || "без ЕДРПОУ"}`
                                                : "Юрлицо не выбрано"}
                                            readOnly
                                        />
                                    </Field>
                                </div>
                                <button
                                    type="button"
                                    className={`og-check${storeDraft.is_active ? " ck" : ""}`}
                                    onClick={() => onStoreChange("is_active", !storeDraft.is_active)}
                                >
                                    <span className="box">{storeDraft.is_active ? "✓" : ""}</span>
                                    <span>Магазин активен</span>
                                </button>
                                {[
                                    ["stock_enabled", "Остатки магазина включены"],
                                    ["orders_enabled", "Заказы магазина включены"],
                                ].map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`og-check${storeDraft[key] ? " ck" : ""}`}
                                        onClick={() => onStoreChange(key, !storeDraft[key])}
                                    >
                                        <span className="box">{storeDraft[key] ? "✓" : ""}</span>
                                        <span>{label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="og-block">
                            <div className="og-bh"><span className="ic">⇄</span><h4>Branch и интеграции</h4></div>
                            <p className="og-mini">
                                Branch магазина используется как branch в stock payload и routing заказов.
                            </p>
                            <div className="og-grid2">
                                <Field label="Branch магазина">
                                    {mappingBranchOptions.length > 0 ? (
                                        <select
                                            className="og-inp"
                                            value={storeDraft.tabletki_branch}
                                            onChange={(event) => onStoreChange("tabletki_branch", event.target.value)}
                                            disabled={storeRoutingReadOnly || mappingBranchesLoading}
                                        >
                                            {currentStoreBranchMissingFromOptions && storeDraft.tabletki_branch ? (
                                                <option value={storeDraft.tabletki_branch}>
                                                    {`${storeDraft.tabletki_branch} — текущий branch вне Business mapping`}
                                                </option>
                                            ) : null}
                                            <option value="">
                                                {mappingBranchesLoading ? "Загрузка branch..." : "Выберите branch Business-контура"}
                                            </option>
                                            {mappingBranchOptions.map((item) => (
                                                <option key={item.branch} value={item.branch}>
                                                    {item.is_primary_enterprise_branch
                                                        ? `${item.branch} — основной branch предприятия`
                                                        : `${item.branch}${item.source === "mapping_branch_fallback" ? " — legacy fallback" : ""}`}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            className="og-inp"
                                            value={storeDraft.tabletki_branch}
                                            onChange={(event) => onStoreChange("tabletki_branch", event.target.value)}
                                            disabled={storeRoutingReadOnly || mappingBranchesLoading}
                                            placeholder={mappingBranchesLoading ? "Загрузка branch..." : "Введите branch Business-контура"}
                                        />
                                    )}
                                </Field>
                                <Field label="SalesDrive ID">
                                    <input
                                        type="number"
                                        className="og-inp"
                                        value={selectedBusinessOrganization?.salesdrive_organization_id || storeDraft.salesdrive_enterprise_id || ""}
                                        readOnly
                                    />
                                </Field>
                            </div>
                            {mappingBranchesError ? (
                                <div className="og-callout danger">
                                    <span className="ic">⚠</span>
                                    <div>{mappingBranchesError}</div>
                                </div>
                            ) : null}
                            {!mappingBranchesLoading && mappingBranchOptions.length === 0 ? (
                                <div className="og-callout warn">
                                    <span className="ic">⚠</span>
                                    <div>Для выбранного предприятия пока нет branch в Business mapping. При сохранении магазина branch будет создан в business_store_branch_mappings.</div>
                                </div>
                            ) : null}
                            {currentStoreBranchMissingFromOptions ? (
                                <div className="og-callout danger">
                                    <span className="ic">⚠</span>
                                    <div>Текущий branch магазина отсутствует в Business mapping выбранного предприятия.</div>
                                </div>
                            ) : null}
                        </div>

                    </Section>
                    ) : null}

                    {showStoreSections && selectedStoreId ? (
                        <DynamicMarkupSchedulePanel storeId={selectedStoreId} />
                    ) : null}

                    {showStoreSections && selectedStoreId ? (
                        <ProductPriceOverridesPanel storeId={selectedStoreId} />
                    ) : null}

                    {showStoreSections ? (
                    <Section
                        title="Branch-маппинг предприятия"
                        description="Рабочий список branch из Business-маппинга для выбранного Business-предприятия."
                    >
                        {mappingBranchOptions.length > 0 ? (
                            <div className="og-tbl-wrap">
                                <table className="og-tbl">
                                    <thead>
                                        <tr>
                                            {["Branch", "Внешний store / mapping", "Роль"].map((header) => (
                                                <th key={header}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mappingBranchOptions.map((item) => (
                                            <tr key={item.branch}>
                                                <td className="strong">{item.branch}</td>
                                                <td>{item.mapping_store_hint || emptyValue}</td>
                                                <td>
                                                    {item.is_primary_enterprise_branch
                                                        ? <span className="og-pill g">Основной branch предприятия</span>
                                                        : <span className="og-pill">Branch магазина</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="og-callout warn">
                                <span className="ic">⚠</span>
                                <div>Для выбранного предприятия нет branch в Business-маппинге. При сохранении магазина branch будет создан автоматически.</div>
                            </div>
                        )}
                    </Section>
                    ) : null}

                    {!showStoreSections ? (
                    <Section
                        title="Магазины предприятия"
                        description="Операционный список store overlays выбранного Business-предприятия."
                    >
                        {storesForSelectedEnterprise.length > 0 ? (
                        <div className="og-tbl-wrap">
                            <table className="og-tbl">
                                <thead>
                                    <tr>
                                        {[
                                            "Код магазина",
                                            "Торговая марка",
                                            "Branch",
                                            "Активен",
                                            "Остатки",
                                            "Заказы",
                                            "SalesDrive ID",
                                            ...(showStoreSections ? ["Действия"] : []),
                                        ].map((header) => (
                                            <th key={header}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {storesForSelectedEnterprise.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={showStoreSections ? "click" : undefined}
                                            style={item.id === selectedStoreId ? { backgroundColor: "var(--og-accent-soft)" } : undefined}
                                            onClick={() => {
                                                if (showStoreSections) {
                                                    selectOverlay(item);
                                                }
                                            }}
                                        >
                                            <td className="strong">{item.store_code}</td>
                                            <td>{item.trade_brand || emptyValue}</td>
                                            <td>{item.tabletki_branch || emptyValue}</td>
                                            <td><span className={item.is_active ? "og-pill g" : "og-pill off"}>{item.is_active ? "Да" : "Нет"}</span></td>
                                            <td><span className={item.stock_enabled ? "og-pill g" : "og-pill off"}>{boolShort(item.stock_enabled)}</span></td>
                                            <td><span className={item.orders_enabled ? "og-pill g" : "og-pill off"}>{boolShort(item.orders_enabled)}</span></td>
                                            <td>{item.salesdrive_enterprise_id ?? emptyValue}</td>
                                            {showStoreSections ? (
                                                <td className="go">
                                                    <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => selectOverlay(item)}>
                                                        Открыть
                                                    </button>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        ) : (
                            <div className="og-callout">
                                <span className="ic">ℹ</span>
                                <div>Для выбранного предприятия пока нет сохранённых магазинов. Заполните форму выше и сохраните первый store overlay.</div>
                            </div>
                        )}
                    </Section>
                    ) : null}
                </>
            ) : null}
                </div>
            </div>
        </div>
    );
};

export default BusinessStoresPage;
